'use client'

import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { cn } from '~/lib/utils'
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/modules/common/components/ui/form'

import { PasswordChecklist } from './password-checklist'
import { Input } from './ui/input'

type AnyControllerRenderProps = ControllerRenderProps<
  FieldValues,
  Path<FieldValues>
>

type PasswordInputProps = {
  field: AnyControllerRenderProps
  showChecklist?: boolean
  password?: string
  confirmWith?: string
  label?: string
  missingLabel?: string
}

export function PasswordInput({
  field,
  showChecklist = false,
  password = '',
  confirmWith = '',
  label = '',
  missingLabel = '',
}: PasswordInputProps) {
  const t = useTranslations('page.reset.form')
  const [showPassword, setShowPassword] = useState(false)
  const hasError = confirmWith && password !== confirmWith
  const resolvedLabel = label || t('inputs.password.label')
  const resolvedMissingLabel = missingLabel || t('errors.passwordsMismatch')

  return (
    <FormItem>
      <FormLabel>{resolvedLabel}</FormLabel>
      <div className="relative">
        <FormControl>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="********"
            autoComplete="new-password"
            {...field}
            className={cn(
              {
                'border-destructive': hasError,
                'animate-pulse': hasError,
              },
              'font-[BlinkMacSystemFont,_Roboto,_Helvetica,_Arial,_sans-serif]',
            )}
          />
        </FormControl>
        {field.value.length > 0 && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-primary transition-colors"
          >
            {showPassword ? (
              <EyeOff className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </button>
        )}
      </div>
      {showChecklist && <PasswordChecklist password={password} />}
      {hasError && (
        <FormMessage className="text-destructive animate-fade-in">
          {resolvedMissingLabel}
        </FormMessage>
      )}
      <FormMessage />
    </FormItem>
  )
}
